import React, { useEffect, useState } from "react";
import QrScanButton from "../components/QrScanButton";
import { useScannerInput } from "../lib/scannerInput";
import api, { fmtErr } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScanLine, PackageOpen, RotateCcw, Layers, X, Send, CheckCircle2, XCircle, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const OUT_TYPES = ["Sale", "Distribution", "Transfer", "Return", "Festival Use"];
const FALLBACK_COUNTERS = ["Sarees Contract Sales", "Sarees Devasthanam Sales"];
const EMPTY_OUT = { out_type: "", located_at: "", indent_no: "", indent_date: "", total_out: 1, sanction_amount: "", sanction_description: "", description: "", indent_file: null };

export default function SareeGodownOut() {
  const [code, setCode] = useState("");
  const [token, setToken] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(EMPTY_OUT);
  const [bulk, setBulk] = useState(false);
  const [bulkList, setBulkList] = useState([]);
  const [results, setResults] = useState(null);
  const [saleCounters, setSaleCounters] = useState([]);

  useEffect(() => { api.get("/sarees/sale-counters").then((r) => setSaleCounters(r.data)).catch(() => {}); }, []);
  const counterOptions = saleCounters.length ? saleCounters.map((c) => c.name) : FALLBACK_COUNTERS;
  const setO = (k, v) => setOut((o) => ({ ...o, [k]: v }));

  const scanner = useScannerInput((v) => { setCode(v); search(v); });
  const search = async (val) => {
    const c = typeof val === "string" ? val : code;
    if (!c.trim()) return;
    setLoading(true);
    setDone(false);
    try {
      const t = (await api.get(`/sarees/${encodeURIComponent(c.trim())}`)).data;
      if (bulk) {
        if (t.status !== "GODOWN_IN") toast.error(`${t.token_no}: status is ${t.status} — only godown-in stock can be taken out`);
        else if (bulkList.some((x) => x.token_no === t.token_no)) toast.error(`${t.token_no} is already in the list`);
        else setBulkList((l) => [...l, t]);
        setCode("");
      } else {
        setToken(t);
      }
    } catch (e) {
      if (!bulk) setToken(null);
      toast.error(fmtErr(e));
    } finally { setLoading(false); }
  };

  const outPayload = () => ({ ...out, total_out: Number(out.total_out) || 1, sanction_amount: Number(out.sanction_amount) || 0 });

  const submit = async () => {
    if (!out.out_type) { toast.error("Out Type is required"); return; }
    setLoading(true);
    try {
      await api.post(`/sarees/${token.token_no}/godown-out`, outPayload());
      search();
      setDone(true);
      toast.success("Godown Out recorded");
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const sendAll = async () => {
    if (!bulkList.length) { toast.error("Scan at least one token"); return; }
    if (!out.out_type) { toast.error("Out Type is required"); return; }
    setLoading(true);
    try {
      const r = await api.post("/sarees/bulk-godown-out", { token_nos: bulkList.map((t) => t.token_no), ...outPayload() });
      setResults(r.data.results);
      setBulkList([]);
      toast.success(`Godown Out — ${r.data.done} done, ${r.data.failed} failed`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const reset = () => { setToken(null); setCode(""); setDone(false); setOut(EMPTY_OUT); setBulkList([]); setResults(null); };

  // Indent copy (PDF / JPG / PNG, up to 2.5 MB): Super Admin / Temple Admin can open it later from the Saree Report.
  const pickIndent = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) { toast.error("Upload a PDF, JPG or PNG file"); return; }
    if (file.size > 2.5 * 1024 * 1024) { toast.error("File is too large (max 2.5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setO("indent_file", { name: file.name, data: reader.result });
    reader.readAsDataURL(file);
  };

  const outFields = (p, actionBtn) => (
    <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div>
        <Label className="text-xs">Out Type *</Label>
        <Select value={out.out_type} onValueChange={(v) => setOut((o) => ({ ...o, out_type: v, located_at: "" }))}>
          <SelectTrigger data-testid={`${p}-type-select`}><SelectValue placeholder="Out Type…" /></SelectTrigger>
          <SelectContent>{OUT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Located at</Label>
        {out.out_type === "Sale" ? (
          <Select value={out.located_at} onValueChange={(v) => setO("located_at", v)}>
            <SelectTrigger data-testid={`${p}-located`}><SelectValue placeholder="Select sales counter…" /></SelectTrigger>
            <SelectContent>{counterOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input value={out.located_at} onChange={(e) => setO("located_at", e.target.value)} data-testid={`${p}-located`} />
        )}
      </div>
      <div><Label className="text-xs">Indent No</Label><Input value={out.indent_no} onChange={(e) => setO("indent_no", e.target.value)} data-testid={`${p}-indent-no`} /></div>
      <div><Label className="text-xs">Indent Date</Label><Input type="date" value={out.indent_date} onChange={(e) => setO("indent_date", e.target.value)} data-testid={`${p}-indent-date`} /></div>
      <div><Label className="text-xs">Total Out (qty)</Label><Input type="number" min={1} value={out.total_out} onChange={(e) => setO("total_out", e.target.value)} data-testid={`${p}-total`} /></div>
      <div><Label className="text-xs">Sanction Amount (₹)</Label><Input type="number" min={0} value={out.sanction_amount} onChange={(e) => setO("sanction_amount", e.target.value)} data-testid={`${p}-sanction`} /></div>
      <div><Label className="text-xs">Sanction Description</Label><Input value={out.sanction_description} onChange={(e) => setO("sanction_description", e.target.value)} data-testid={`${p}-sanction-desc`} /></div>
      <div><Label className="text-xs">Description</Label><Input value={out.description} onChange={(e) => setO("description", e.target.value)} data-testid={`${p}-desc`} /></div>
      <div className="col-span-2 md:col-span-4">
        <Label className="text-xs">Indent Copy (PDF / JPG)</Label>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <label className="inline-flex items-center gap-1.5 px-3 h-10 text-sm border border-[#E5D9C5] rounded-md cursor-pointer bg-white hover:bg-gold-50" data-testid={`${p}-indent-upload`}>
            <Upload className="w-4 h-4" /> {out.indent_file ? "Change file" : "Upload indent copy"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={pickIndent} data-testid={`${p}-indent-file`} />
          </label>
          {out.indent_file && (
            <span className="inline-flex items-center gap-1.5 text-sm text-maroon-900 bg-gold-50 border border-gold-300 rounded-md px-2.5 h-10" data-testid={`${p}-indent-name`}>
              <FileText className="w-4 h-4" /> {out.indent_file.name}
              <button type="button" onClick={() => setO("indent_file", null)} title="Remove" className="ml-1"><X className="w-4 h-4 text-red-600" /></button>
            </span>
          )}
        </div>
      </div>
      {actionBtn}
    </CardContent>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="godown-out-page">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900">Sarees — Godown Out</h1>
        <Button variant={bulk ? "default" : "outline"} data-testid="godownout-bulk-toggle"
          onClick={() => { setBulk(!bulk); setToken(null); setDone(false); setResults(null); setCode(""); }}
          className={bulk ? "bg-maroon-700 hover:bg-maroon-800 text-white" : ""}>
          <Layers className="w-4 h-4 mr-1" /> {bulk ? "Bulk Mode: ON" : "Bulk Mode"}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-5">{bulk ? "Scan tokens one by one to build the list, fill the common details once, then Send All." : "Scan the verified token, fill the godown-out details and record the stock movement."}</p>

      <div className="scan-row flex gap-2 mb-6">
        <Input ref={scanner.ref} autoFocus data-testid="godownout-code-input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder={bulk ? "Scan QRCode / Token No — adds to list" : "Scan QRCode / enter Token No"} className="h-12 font-mono" onKeyDown={scanner.onKeyDown} />
        <QrScanButton onScan={(t) => { setCode(t); search(t); scanner.refocus(); }} label="Camera" />
        <Button data-testid="godownout-verify-btn" onClick={search} disabled={loading} className="h-12 px-6 bg-maroon-700 hover:bg-maroon-800 text-white">
          <ScanLine className="w-5 h-5 mr-2" /> {bulk ? "Add" : "Verify"}
        </Button>
        <Button variant="outline" onClick={reset} className="h-12" data-testid="godownout-reset-btn"><RotateCcw className="w-4 h-4" /></Button>
      </div>

      {bulk && (
        <div className="space-y-5">
          <Card className="card-gold-top shadow-sm" data-testid="bulk-list-card">
            <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Scanned Tokens ({bulkList.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {bulkList.length === 0 && <p className="text-sm text-slate-400 text-center py-4" data-testid="bulk-empty">No tokens scanned yet — scan the access card QR or type the token number and press Add.</p>}
              {bulkList.map((t) => (
                <div key={t.token_no} className="flex items-center gap-3 border border-[#E5D9C5] rounded-md p-2" data-testid={`bulk-row-${t.token_no}`}>
                  {t.photo ? <img src={t.photo} alt="saree" className="w-10 h-12 object-cover rounded border border-gold-300" />
                    : <div className="w-10 h-12 rounded border border-dashed border-gold-300 text-[8px] text-slate-400 flex items-center justify-center">No photo</div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs">{t.token_no}</p>
                    <p className="text-xs text-slate-500">{t.saree_type_name} · {t.category} · ₹{t.rate}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setBulkList((l) => l.filter((x) => x.token_no !== t.token_no))} data-testid={`bulk-remove-${t.token_no}`}><X className="w-4 h-4 text-red-600" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="card-gold-top shadow-sm" data-testid="bulk-form">
            <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Common Godown Out Details (applies to all scanned tokens)</CardTitle></CardHeader>
            {outFields("bulk", (
              <Button onClick={sendAll} disabled={loading || !bulkList.length} data-testid="bulk-send-btn"
                className="col-span-2 md:col-span-4 h-12 bg-pink-700 hover:bg-pink-800 text-white font-heading tracking-widest">
                <Send className="w-5 h-5 mr-2" /> SEND ALL ({bulkList.length})
              </Button>
            ))}
          </Card>

          {results && (
            <Card className="shadow-sm" data-testid="bulk-results">
              <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Results</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {results.map((r) => (
                  <div key={r.token_no} data-testid={`bulk-result-${r.token_no}`}
                    className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 border ${r.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
                    {r.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    <span className="font-mono text-xs">{r.token_no}</span>
                    {!r.ok && <span className="text-xs">— {r.error}</span>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {!bulk && token && (
        <div className="space-y-5">
          <Card className="card-gold-top shadow-sm" data-testid="godownout-details">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg text-maroon-900">Token Details</CardTitle>
              <StatusBadge status={token.status} saree />
            </CardHeader>
            <CardContent className="flex gap-4 text-sm">
              {token.photo ? (
                <img src={token.photo} alt="saree" className="w-28 h-32 object-cover rounded-md border-2 border-gold-400 shrink-0" data-testid="godownout-saree-photo" />
              ) : (
                <div className="w-28 h-32 rounded-md border-2 border-dashed border-gold-400 flex items-center justify-center text-[10px] text-slate-400 shrink-0" data-testid="godownout-no-photo">No photo</div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 flex-1">
                <div><p className="text-xs text-gold-700 font-bold uppercase">Token No</p><p className="font-mono">{token.token_no}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Category</p><p>{token.category}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Saree Type</p><p>{token.saree_type_name}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Rate</p><p>₹{token.rate}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Phone</p><p>{token.phone || "-"}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Sanction</p><p>₹{token.sanction_amount || 0}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Verified By</p><p>{token.verified_by || "-"}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Date</p><p>{new Date(token.created_at).toLocaleDateString("en-IN")}</p></div>
              </div>
            </CardContent>
          </Card>

          {token.status === "GODOWN_IN" && !done && (
            <Card className="card-gold-top shadow-sm" data-testid="godownout-form">
              <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Godown Out Details</CardTitle></CardHeader>
              {outFields("godownout", (
                <Button onClick={submit} disabled={loading} data-testid="godownout-submit-btn"
                  className="col-span-2 md:col-span-4 h-12 bg-pink-700 hover:bg-pink-800 text-white font-heading tracking-widest">
                  <PackageOpen className="w-5 h-5 mr-2" /> GODOWN OUT
                </Button>
              ))}
            </Card>
          )}
          {token.status === "ISSUED" && (
            <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800" data-testid="godownout-not-in">
              This token is not verified into the godown yet. Complete Godown In first.
            </p>
          )}
          {done && (
            <div className="rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-900 p-4 text-center font-heading font-bold" data-testid="godownout-done">
              GODOWN OUT RECORDED — Token {token.token_no}
            </div>
          )}
          {token.status === "GODOWN_OUT" && !done && (
            <p className="text-sm text-slate-500" data-testid="godownout-already">Already taken out by {token.out_by} on {new Date(token.out_at).toLocaleString("en-IN")} ({token.out_type}).</p>
          )}
        </div>
      )}
    </div>
  );
}
